<template>
  <Layout>
    <Head :title="title" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ invite.appointment_type_name || invite.legal_case_type_name }}</h1>
          <p class="text-gray-600 mt-1">{{ translations.scheduleAppointment }}</p>
        </div>

        <!-- Cancellation Notice -->
        <Card v-if="invite.scheduling_status === 'C'" class="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle class="text-red-900">{{ translations.cancelled }}</CardTitle>
          </CardHeader>
          <CardContent>
            <p class="text-red-800">{{ translations.cancelledMessage }}</p>
          </CardContent>
        </Card>

        <!-- Request Details -->
        <Card>
          <CardHeader>
            <CardTitle>{{ translations.requestDetails }}</CardTitle>
            <CardDescription>{{ invite.legal_case_type_description }}</CardDescription>
          </CardHeader>
          <CardContent>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div v-if="invite.location" class="flex items-start gap-2 text-gray-600">
                <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <span class="font-medium">{{ translations.location }}</span>
                  <span class="ml-1">{{ invite.location }}</span>
                </div>
              </div>

              <div v-if="invite.primary_attorney_name" class="flex items-start gap-2 text-gray-600">
                <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div>
                  <span class="font-medium">{{ translations.primaryAttorney }}</span>
                  <span class="ml-1">{{ invite.primary_attorney_name }}</span>
                  <span v-if="invite.primary_attorney_email" class="block text-xs text-gray-500">
                    {{ invite.primary_attorney_email }}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <!-- Available Slots -->
        <Card v-if="invite.scheduling_status !== 'C'">
          <CardHeader>
            <CardTitle>{{ translations.availableSlots }}</CardTitle>
            <CardDescription>
              <template v-if="slots.length > 0">
                {{ translations.selectSlot }}
              </template>
              <template v-else>
                {{ translations.noSlots }}
              </template>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <template v-if="slots.length > 0">
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div
                  v-for="slot in slots"
                  :key="slot.id"
                  class="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div class="space-y-3">
                    <div>
                      <h3 class="font-medium text-gray-900">{{ slot.title }}</h3>
                    </div>
                    <div class="space-y-2 text-sm text-gray-600">
                      <div class="flex items-center gap-2">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{{ translations.starts }} {{ formatDateTime(slot.start_date) }}</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{{ translations.ends }} {{ formatDateTime(slot.end_date) }}</span>
                      </div>
                    </div>
                    <div class="pt-2">
                      <Button
                        class="w-full"
                        @click="handleSlotClick(slot)"
                      >
                        {{ translations.bookSlot }}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="text-center py-12">
                <div class="mx-auto h-24 w-24 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="mt-4 text-sm font-medium text-gray-900">{{ translations.noAvailableSlots }}</h3>
                <p class="mt-2 text-sm text-gray-500">
                  {{ translations.contactAttorney }}
                </p>
              </div>
            </template>
          </CardContent>
        </Card>
      </div>
    </div>

    <!-- Booking Confirmation Dialog -->
    <Dialog v-model:open="showConfirmDialog">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ translations.confirmBooking }}</DialogTitle>
          <DialogDescription>
            {{ translations.confirmBookingMessage }}
          </DialogDescription>
        </DialogHeader>
        <div v-if="selectedSlot" class="py-4">
          <div class="space-y-3">
            <div>
              <h3 class="font-medium text-gray-900">{{ selectedSlot.title }}</h3>
            </div>
            <div class="space-y-2 text-sm text-gray-600">
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{{ translations.starts }} {{ formatDateTime(selectedSlot.start_date) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{{ translations.ends }} {{ formatDateTime(selectedSlot.end_date) }}</span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelBooking">
            {{ translations.cancel }}
          </Button>
          <Button @click="confirmBooking">
            {{ translations.confirm }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Layout>
</template>

<script setup>
import { Head, router, usePage } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '@/entrypoints/translations.js'

// Props
const { invite, isolated } = defineProps({
  invite: Object,
  isolated: { type: Boolean, required: false, default: false },
  slots: Array,
  tokenized_id: String,
})

// Get current locale from page props and make reactive
const page = usePage()
const currentLocale = computed(() => page.props.locale?.current || 'en')
const reactiveLocale = ref(currentLocale.value)

// Watch for locale changes and update reactive locale
watch(currentLocale, (newLocale) => {
  reactiveLocale.value = newLocale
})

// Also listen for window locale changes (from LanguageSelector)
watch(() => window.__currentLocale__, (newLocale) => {
  if (newLocale && newLocale !== reactiveLocale.value) {
    reactiveLocale.value = newLocale
  }
}, { immediate: true })

// Computed properties for reactive translations
const translations = computed(() => {
  const locale = reactiveLocale.value
  return {
    scheduleAppointment: t('invites.schedule_appointment', { locale }),
    requestDetails: t('invites.request_details', { locale }),
    location: t('common.location', { locale }),
    primaryAttorney: t('common.primary_attorney', { locale }),
    availableSlots: t('invites.available_slots', { locale }),
    selectSlot: t('invites.select_slot', { locale }),
    noSlots: t('invites.no_slots', { locale }),
    starts: t('invites.starts', { locale }),
    ends: t('invites.ends', { locale }),
    bookSlot: t('invites.book_slot', { locale }),
    noAvailableSlots: t('invites.no_available_slots', { locale }),
    contactAttorney: t('invites.contact_attorney', { locale }),
    cancelled: t('invites.request_cancelled', { locale }),
    cancelledMessage: t('invites.cancelled_message', { locale }),
    confirmBooking: t('invites.confirm_booking', { locale }),
    confirmBookingMessage: t('invites.confirm_booking_message', { locale }),
    confirm: t('common.confirm', { locale }),
    cancel: t('common.cancel', { locale })
  }
})

// Instance Variables
const title = `${invite.appointment_type_name || invite.legal_case_type_name} - Pending Request - Kiosk`
const showConfirmDialog = ref(false)
const selectedSlot = ref(null)

// Create computed formatter based on current locale
const dateFormatter = computed(() => {
  const locale = reactiveLocale.value === 'es' ? 'es-ES' : 'en-US'
  return {
    format: (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    }
  }
})

// Helper function to format date and time (reactive to locale changes)
const formatDateTime = (dateString) => {
  return dateFormatter.value.format(dateString)
}

const linkParams = slot => ({
  invite_id: invite.id,
  slot_end_datetime: slot.end_date,
  slot_id: slot.id,
  slot_start_datetime: slot.start_date,
})

const bookingLink = isolated ? `/i/${invite.tokenized_id}` : '/bookings'

const handleSlotClick = (slot) => {
  selectedSlot.value = slot
  showConfirmDialog.value = true
}

const confirmBooking = () => {
  if (!selectedSlot.value) return
  router.post(bookingLink, linkParams(selectedSlot.value))
  showConfirmDialog.value = false
}

const cancelBooking = () => {
  selectedSlot.value = null
  showConfirmDialog.value = false
}
</script>
